import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format as formatDateFn } from "date-fns";

import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";

import en from "date-fns/locale/en-GB";
import fr from "date-fns/locale/fr";
import es from "date-fns/locale/es";
import de from "date-fns/locale/de";
import it from "date-fns/locale/it";
import nl from "date-fns/locale/nl";
import pl from "date-fns/locale/pl";
import pt from "date-fns/locale/pt";
import cs from "date-fns/locale/cs";
import { Label } from "@fluentui/react-components";

// Register locales
registerLocale("en", en);
registerLocale("fr", fr);
registerLocale("es", es);
registerLocale("de", de);
registerLocale("it", it);
registerLocale("nl", nl);
registerLocale("pl", pl);
registerLocale("pt", pt);
registerLocale("cs", cs);

// Irish ("ga") has no date-fns locale, so react-datepicker can't localize its month header or
// weekday names and falls back to English. For Irish ONLY we translate via the app's own i18n
// dictionary; every other language keeps its existing date-fns localization untouched.
const IRISH_LOCALE_CODE = "ga";

// Weekday column headers, indexed by display order. calendarStartDay={1} fixes that order to
// Monday→Sunday, and react-datepicker calls formatWeekDay once per column in that order, so a
// per-render counter maps each column to the right key.
const DATEPICKER_WEEKDAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const makeWeekdayHeaderFormatter = (t) => {
  let columnIndex = 0;
  return () => t(DATEPICKER_WEEKDAY_KEYS[columnIndex++ % 7]);
};

const CustomDatePicker = ({
  selected,
  value,
  onChange,
  id = "datepicker",
  dateFormat = "dd/MM/yyyy",
  minDate,
  shouldCloseOnSelect = true,
  wrapperClass = "",
  preventMonthAutoSelect = false,
  formatWeekDay: formatWeekDayProp,
  renderCustomHeader: renderCustomHeaderProp,
  ...props
}) => {
  const [internalDate, setInternalDate] = useState(
    selected ?? value ?? null
  );

  const [initialDate, setInitialDate] = useState(null);
  const [isChanged, setIsChanged] = useState(false);
  const [pickerMonth, setPickerMonth] = useState(selected ?? value ?? new Date());

  const { t, i18n } = useTranslation();
  const locale = i18n.language.split("-")[0];
  const isIrish = locale === IRISH_LOCALE_CODE;

  // sync state
  useEffect(() => {
    if (selected !== undefined) {
      setInternalDate(selected);
    } else if (value !== undefined) {
      setInternalDate(value);
    }
  }, [selected, value]);

  useEffect(() => {
    if (internalDate) {
      setPickerMonth(internalDate);
    }
  }, [internalDate]);

  // store initial date once
  useEffect(() => {
    if (internalDate && !initialDate) {
      setInitialDate(internalDate);
    }
  }, [internalDate]);

  const handleChange = (date) => {
    if (!date) return;

    if (initialDate) {
      const changed =
        new Date(date).getTime() !== new Date(initialDate).getTime();
      setIsChanged(changed);
    } else {
      setInitialDate(date);
      setIsChanged(true);
    }

    if (onChange) {
      onChange(date);
    } else {
      setInternalDate(date);
    }
  };

  const isSameMonthAndYear = (date1, date2) => {
    if (!date1 || !date2) return false;

    return (
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const shouldHideKeyboardSelection =
    preventMonthAutoSelect && !isSameMonthAndYear(internalDate, pickerMonth);

  const calendarClassName = [
    "custom-date-picker-calendar",
    shouldHideKeyboardSelection ? "datepicker-hide-keyboard-selection" : "",
    props.calendarClassName || "",
  ]
    .filter(Boolean)
    .join(" ");

  const inputValue = internalDate
    ? formatDateFn(internalDate, dateFormat)
    : "";

  const handleCalendarOpen = () => {
    setPickerMonth(internalDate || new Date());
    props.onCalendarOpen?.();
  };

  const handleMonthChange = (date) => {
    setPickerMonth(date);
    props.onMonthChange?.(date);
  };

  // Irish-only month/year header. The stable English month name is the i18n key
  // (e.g. "June" -> "Meitheamh"); nav buttons reuse react-datepicker's own classes for styling.
  const renderIrishHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => {
    const englishMonth = new Intl.DateTimeFormat("en-US", { month: "long" }).format(date);
    return (
      <div className="react-datepicker__current-month">
        <button
          type="button"
          aria-label="Previous Month"
          className="react-datepicker__navigation react-datepicker__navigation--previous"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
        >
          <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--previous" />
        </button>
        <span>{`${t(englishMonth)} ${date.getFullYear()}`}</span>
        <button
          type="button"
          aria-label="Next Month"
          className="react-datepicker__navigation react-datepicker__navigation--next"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
        >
          <span className="react-datepicker__navigation-icon react-datepicker__navigation-icon--next" />
        </button>
      </div>
    );
  };

  // Apply translated header/weekdays only for Irish; other languages keep date-fns behavior
  // (and any header/formatter a caller passed in).
  const resolvedFormatWeekDay = isIrish ? makeWeekdayHeaderFormatter(t) : formatWeekDayProp;
  const resolvedRenderCustomHeader = isIrish ? renderIrishHeader : renderCustomHeaderProp;

  return (
    <div
      className={`date-input position-relative ${
        isChanged ? "ctm-success" : ""
      } ${wrapperClass}`}
    >
    
      <DatePicker
        locale={locale}
        id={id}
        className="form-control bg-white"
        selected={
          preventMonthAutoSelect && !isSameMonthAndYear(internalDate, pickerMonth)
            ? null
            : internalDate
        }
        value={preventMonthAutoSelect ? inputValue : undefined}
        openToDate={preventMonthAutoSelect ? pickerMonth : undefined}
        calendarClassName={calendarClassName}
        onChange={handleChange}
        onCalendarOpen={handleCalendarOpen}
        onMonthChange={handleMonthChange}
        dateFormat={dateFormat}
        minDate={minDate}
        shouldCloseOnSelect={shouldCloseOnSelect}
        showPopperArrow={false}
        calendarStartDay={1}
        formatWeekDay={resolvedFormatWeekDay}
        renderCustomHeader={resolvedRenderCustomHeader}
        {...props}
      />
    
    </div>
  );
};

export default CustomDatePicker;
